import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDoc,
  doc,
} from "firebase/firestore";
import { firebaseDB, COLLECTIONS } from "../store/firebaseApp";
import { ACCESS_QUERY_STATUSES } from "./entitlementAccess";

/**
 * Optimized query function with error handling and caching
 * @param {string} collectionName - Name of the collection
 * @param {Array} conditions - Array of where conditions
 * @param {Object} options - Query options (orderBy, limit, etc.)
 * @returns {Promise<Array>} - Array of documents with IDs
 */
export const optimizedQuery = async (collectionName, conditions = [], options = {}) => {
  try {
    const collectionRef = collection(firebaseDB, collectionName);

    // Build all query constraints in an array
    const constraints = [];

    // Apply where conditions
    conditions.forEach((condition) => {
      constraints.push(where(condition.field, condition.operator, condition.value));
    });

    // Apply orderBy if specified
    if (options.orderBy) {
      constraints.push(orderBy(options.orderBy.field, options.orderBy.direction || "asc"));
    }

    // Apply limit if specified
    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    // Apply startAfter if specified (for pagination)
    if (options.startAfter) {
      constraints.push(startAfter(options.startAfter));
    }

    // Build the query with all constraints at once (Firebase v11 requirement)
    const q = query(collectionRef, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error in optimizedQuery for ${collectionName}:`, error);
    throw error;
  }
};

const createdAtMillis = (programming) => {
  const createdAt = programming.createdAt;
  if (typeof createdAt?.toMillis === "function") return createdAt.toMillis();
  if (createdAt instanceof Date) return createdAt.getTime();
  return 0;
};

// Own programmes, plus the gym's for a legacy box coach minus PT ones: excluded server-side
// when ptExcludedServerSide is on, dropped client-side always (a missing isPT stays listed).
export const getUserProgramming = async (
  userUID,
  activeOnly = true,
  userClaims = null,
  { ptExcludedServerSide = false } = {}
) => {
  try {
    const activeCondition = activeOnly ? [{ field: "active", operator: "==", value: true }] : [];
    const newestFirst = { orderBy: { field: "createdAt", direction: "desc" } };

    const isLegacyBoxCoach =
      userClaims?.boxCoach === true && userClaims.coachType === "box" && Boolean(userClaims.boxID);

    const [own, gym] = await Promise.all([
      optimizedQuery(
        COLLECTIONS.Programmings,
        [{ field: "ownerUID", operator: "==", value: userUID }, ...activeCondition],
        newestFirst
      ),
      isLegacyBoxCoach
        ? optimizedQuery(
            COLLECTIONS.Programmings,
            [
              { field: "boxID", operator: "==", value: userClaims.boxID },
              ...activeCondition,
              ...(ptExcludedServerSide ? [{ field: "isPT", operator: "==", value: false }] : []),
            ],
            newestFirst
          )
        : Promise.resolve([]),
    ]);

    const byId = new Map(own.map((programming) => [programming.id, programming]));
    gym.forEach((programming) => {
      if (programming.isPT !== true && !byId.has(programming.id)) {
        byId.set(programming.id, programming);
      }
    });

    return [...byId.values()].sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
  } catch (error) {
    console.error("Error getting user programming:", error);
    throw error;
  }
};

/**
 * Get user invites with optimized query
 * @param {string} userUID - User ID
 * @returns {Promise<Object>} - Object with sent and received invites
 */
export const getUserInvites = async (userUID) => {
  try {
    const conditions = [{ field: "recipientID", operator: "==", value: userUID }];

    const receivedInvites = await optimizedQuery(COLLECTIONS.Invites, conditions);

    const sentConditions = [{ field: "senderID", operator: "==", value: userUID }];

    const sentInvites = await optimizedQuery(COLLECTIONS.Invites, sentConditions);

    return {
      sent: sentInvites,
      received: receivedInvites,
    };
  } catch (error) {
    console.error("Error getting user invites:", error);
    throw error;
  }
};

/**
 * Get user subscriptions with optimized query
 * @param {string} userUID - User ID
 * @returns {Promise<Array>} - Array of subscription documents
 */
export const getUserSubscriptions = async (userUID) => {
  try {
    const subRef = collection(firebaseDB, COLLECTIONS.Users, userUID, COLLECTIONS.Subscriptions);
    const querySnapshot = await getDocs(subRef);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting user subscriptions:", error);
    throw error;
  }
};

/**
 * Live user subscriptions. Cloud Functions write these documents behind the
 * athlete's back — a commitment purchase finishes provisioning seconds after
 * checkout — so a one-shot read leaves a card claiming "we're setting this up"
 * frozen until the page is reloaded. One user's own subscriptions is a handful
 * of documents, so the listener costs effectively nothing.
 * @param {string} userUID - User ID
 * @param {Function} onNext - Receives the subscription documents on every change
 * @param {Function} [onError] - Receives listener errors
 * @returns {Function} Unsubscribe; the caller MUST call it on unmount
 */
export const subscribeToUserSubscriptions = (userUID, onNext, onError) => {
  const subRef = collection(firebaseDB, COLLECTIONS.Users, userUID, COLLECTIONS.Subscriptions);
  return onSnapshot(
    subRef,
    (querySnapshot) => onNext(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
    (error) => {
      console.error("Error listening to user subscriptions:", error);
      if (onError) onError(error);
    }
  );
};

/**
 * Live view of ONE subscription document.
 *
 * The cancel flow needs the same freshness as the list above for a single
 * membership, and reads one document rather than the whole collection: the
 * lanes write the display mirror behind the athlete's back (the term-end
 * intent, the open-request pointer), so a one-shot read would leave the flow
 * asserting a state the server has already moved past.
 *
 * `null` is passed to `onNext` when the document does not exist — distinct from
 * an error, and the caller must render it as "we could not find this
 * membership" rather than as a loading state that never ends.
 * @param {string} userUID - User ID
 * @param {string} subscriptionId - Subscription document ID
 * @param {Function} onNext - Receives the document (or null) on every change
 * @param {Function} [onError] - Receives listener errors
 * @returns {Function} Unsubscribe; the caller MUST call it on unmount
 */
export const subscribeToUserSubscription = (userUID, subscriptionId, onNext, onError) => {
  const subRef = doc(
    firebaseDB,
    COLLECTIONS.Users,
    userUID,
    COLLECTIONS.Subscriptions,
    subscriptionId
  );
  return onSnapshot(
    subRef,
    (snapshot) => onNext(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    (error) => {
      console.error("Error listening to user subscription:", error);
      if (onError) onError(error);
    }
  );
};

/**
 * Get the user's box memberships that could grant access. Callers must still
 * run `isAccessGrantingEntitlement` on each doc: a `past_due` row only counts
 * inside its dunning grace window, and a dunning-paused row never counts.
 * @param {string} userUID - User ID
 * @returns {Promise<Array>} - Array of box membership documents
 */
export const getUserBoxMemberships = async (userUID) => {
  try {
    const membershipsRef = collection(firebaseDB, COLLECTIONS.Users, userUID, "BoxMemberships");
    const q = query(membershipsRef, where("status", "in", ACCESS_QUERY_STATUSES));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error getting user box memberships:", error);
    throw error;
  }
};

/**
 * Get workout cards for a programming with optimized query
 * @param {string} programmingUID - Programming ID
 * @param {Date} startDate - Start date for filtering
 * @param {Date} endDate - End date for filtering
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} - Array of workout card documents
 */
export const getWorkoutCards = async (
  programmingUID,
  startDate = null,
  endDate = null,
  limit = 50
) => {
  const conditions = [{ field: "programmingUID", operator: "==", value: programmingUID }];

  const options = {
    orderBy: { field: "workoutData.date", direction: "desc" },
    limit: limit,
  };

  let results = await optimizedQuery(COLLECTIONS.WorkoutCards, conditions, options);

  // Filter by date range if provided
  if (startDate && endDate) {
    results = results.filter((card) => {
      const cardDate = card.workoutData?.date?.toDate?.() || new Date(card.workoutData?.date);
      return cardDate >= startDate && cardDate <= endDate;
    });
  }

  return results;
};

/**
 * Get single document with error handling
 * @param {string} collectionName - Collection name
 * @param {string} docId - Document ID
 * @returns {Promise<Object|null>} - Document data or null if not found
 */
export const getDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(firebaseDB, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error(`Error getting document ${docId} from ${collectionName}:`, error);
    throw error;
  }
};

/**
 * Batch get documents for better performance
 * @param {string} collectionName - Collection name
 * @param {Array} docIds - Array of document IDs
 * @returns {Promise<Array>} - Array of documents
 */
export const batchGetDocuments = async (collectionName, docIds) => {
  try {
    const promises = docIds.map((id) => getDocument(collectionName, id));
    const results = await Promise.allSettled(promises);

    return results
      .filter((result) => result.status === "fulfilled" && result.value)
      .map((result) => result.value);
  } catch (error) {
    console.error(`Error in batchGetDocuments for ${collectionName}:`, error);
    throw error;
  }
};
